import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOWNLOAD_ICON from '../../../../assets/svg/download-icon.svg';
import Button from '../../../../components/Button/Button';
import { LANG_TEXT } from '../../../../constants/language';
import { DamagedItemStatus, OrderItemStatus, OrderSummaryData } from '../../../../interfaces/index.types';
import { saveAndShareCsv } from '../../../../libs/file-utils';
import { formatHourMonthDayYear } from '../../../../utils/handleDateTime';
import { ITopic, OrderType } from '../../../Dashboard/order.types';
import { buttonStyle, useStyles } from './styles';

const formatPhoneForCsv = (value?: string | number | null) => {
  const phoneValue = value === null || value === undefined ? '' : String(value).trim();
  return phoneValue ? `="${phoneValue.replace(/"/g, '""')}"` : '';
};

function DownloadCsv({
  csvData,
  showForSuperAdmin,
  showForAdmin,
  summaryData,
  routeType,
}: {
  csvData: any[];
  seatDataMap: Map<string, any>;
  showForSuperAdmin: boolean;
  showForAdmin?: boolean;
  summaryData: Record<string, OrderSummaryData | null>;
  routeType: 'order_history' | 'pre_orders';
}) {
  const showPermissionColumns = showForSuperAdmin || !!showForAdmin;
  const classes = useStyles();
  const { t } = useTranslation();
  const prefix = '_DAMAGED';

  const formattedData = useMemo(() => {
    const processedData = csvData.map(
      ({
        type,
        location_id,
        restaurant_name,
        restaurant_id,
        merchant_id,
        item_customize_settings,
        order_status,
        multi_orders,
        gift_info,
        requested_delivery_time,
        delivery_request,
        completed_time,
        phone,
        ...row
      }) => {
        const deliveryDate = requested_delivery_time ? formatHourMonthDayYear(requested_delivery_time).date : '';
        const deliveryTimeDisplay = requested_delivery_time
          ? delivery_request === 'WHEN_ITS_READY'
            ? t(LANG_TEXT.WHEN_ITS_READY)
            : delivery_request || formatHourMonthDayYear(requested_delivery_time).time
          : '';

      // Format multi_orders as "delivered/total" count
        const multiOrdersFormatted = (() => {
          if (!multi_orders?.length) return '-';
          const done = multi_orders.filter(
            (o: any) => !([OrderItemStatus.PREPARING, 'DAMAGED_PREPARING', 'DAMAGED_CREATED'] as string[]).includes(o.status)
          ).length;
          return `${done}/${multi_orders.length}`;
        })();

        const processedRow: any = {
          ...row,
          order_create_time: formatHourMonthDayYear(row.order_create_time).fullDateTime,
          delivery_request_date: deliveryDate,
          requested_delivery_time: deliveryTimeDisplay,
          completed_time: completed_time ? formatHourMonthDayYear(completed_time).fullDateTime : '',
          multi_orders: multiOrdersFormatted,
          item_name: item_customize_settings?.length > 0 ? `${row.item_name} (${item_customize_settings.map((item: ITopic) => item.name).join(', ')})` : row.item_name,
        };

        // SuperAdmin and Admin (HEADQUARTER) see restaurant/gift/location columns; STAFF does not
        if (showPermissionColumns) {
          processedRow['restaurant_name'] = restaurant_name || '';
          processedRow['restaurant_id'] = restaurant_id || '';
          processedRow['merchant_id'] = merchant_id || '';
          processedRow['location_id'] = location_id || '';
          processedRow['phone'] = formatPhoneForCsv(phone);
          processedRow['gift_receiver_name'] = gift_info?.receiver_name || '';
          processedRow['gift_receiver_phone'] = formatPhoneForCsv(gift_info?.receiver_phone);
          processedRow['gift_receiver_email'] = gift_info?.receiver_email || '';
        }

        return processedRow;
      }
    );

    const calculateSummary = (orders: any[]) => {
      const totalDeliveryFee = orders
        .filter((order) => order.item_food_id === t(LANG_TEXT.DELIVERY_FEE_ETC))
        .reduce((sum, order) => sum + (order.item_price ?? 0), 0);

      const totalOrderAmount = orders.reduce(
        (sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0),
        0
      );
      const totalDamagedFee = orders
        .filter((order) => String(order.id || '').includes(prefix))
        .reduce((sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0), 0);

      const totalFoodRevenue = totalOrderAmount - totalDeliveryFee;
      const totalWithServiceCharge = totalFoodRevenue * 0.1;
      const totalBenefit = totalDeliveryFee + totalWithServiceCharge;
      const totalOrders = new Set(orders.map((order) => order.id)).size;

      const uniqueOrderIds = Array.from(new Set(orders.map((order) => order.id)));

      const deliveredOrderIds = new Set(
        orders
          .filter((order) => order.order_status !== OrderItemStatus.CANCELLED && order.order_status !== DamagedItemStatus.DAMAGED_CANCELLED && !order.id.includes(prefix))
          .map((order) => order.id)
      );
      const totalDeliveredOrder = uniqueOrderIds.filter((id) => deliveredOrderIds.has(id)).length;
      const totalDamagedOrder = uniqueOrderIds.filter((id) => id.includes(prefix)).length;

      const cancelledOrderIds = new Set(orders.filter((order) => order.order_status === OrderItemStatus.CANCELLED).map((order) => order.id));
      const totalCancelledOrder = uniqueOrderIds.filter((id) => cancelledOrderIds.has(id)).length;

      return {
        totalDeliveryFee,
        totalOrderAmount,
        totalFoodRevenue,
        totalDamagedFee,
        totalWithServiceCharge,
        totalBenefit,
        totalOrders,
        totalDeliveredOrder,
        totalDamagedOrder,
        totalCancelledOrder,
      };
    };

    const normalize = (k: string) => k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const availableSummaryKeys = Object.keys(summaryData || {});
    const canonicalOrderHistory = ['regular_order', 'regular_pre_order', 'special_order', 'special_pre_order'];
    const canonicalPreOrders = ['regular_pre_order', 'special_pre_order'];
    const findActualKey = (canonicalKey: string) => {
      const target = normalize(canonicalKey);
      return availableSummaryKeys.find((k) => normalize(k) === target) || availableSummaryKeys.find((k) => normalize(k).includes(target));
    };

    const summaryTypeKeys = (
      routeType === 'pre_orders' ? canonicalPreOrders.map(findActualKey).filter(Boolean) : canonicalOrderHistory.map(findActualKey).filter(Boolean)
    ) as string[];

    const typeStatsMap = Object.fromEntries(
      summaryTypeKeys.map((typeKey) => {
        const target = normalize(typeKey);
        const rows = csvData.filter((o: any) => {
          const actual = normalize(String(o.order_type || ''));
          return actual === target || actual.includes(target) || target.includes(actual);
        });
        return [typeKey, calculateSummary(rows)];
      })
    ) as Record<string, ReturnType<typeof calculateSummary>>;

    const grandStats = calculateSummary(csvData);

    const ORDER_TYPE_LABELS: Record<string, string> = {
      [OrderType.REGULAR_ORDERS]: t(LANG_TEXT.REGULAR_ORDERS),
      [OrderType.REGULAR_PRE_ORDERS]: t(LANG_TEXT.REGULAR_PRE_ORDERS),
      [OrderType.SPECIAL_ORDERS]: t(LANG_TEXT.SPECIAL_ORDERS),
      [OrderType.SPECIAL_PRE_ORDERS]: t(LANG_TEXT.SPECIAL_PRE_ORDERS),
    };

    const VALUE_COL_KEYS = ['seat_number', 'rider_id', 'rider_name', 'item_food_id', 'sku'];
    const COL_A = 'id';
    const COL_ASTROLAB = VALUE_COL_KEYS[summaryTypeKeys.length];

    const typeColKey = Object.fromEntries(
      summaryTypeKeys.map((ot, i) => [ot, VALUE_COL_KEYS[i]])
    );

    const tv = (ot: string, key: keyof ReturnType<typeof calculateSummary>) => {
      const stats = typeStatsMap[ot];
      if (!stats) return 0;
      return stats[key] ?? 0;
    };

    processedData.push({});
    const headerRow: any = { [COL_A]: t(LANG_TEXT.TOTAL_REVENUE) };
    summaryTypeKeys.forEach((ot) => {
      headerRow[typeColKey[ot]] = ORDER_TYPE_LABELS[ot] || ot.toUpperCase();
    });
    if (showForSuperAdmin) headerRow[COL_ASTROLAB] = t(LANG_TEXT.ASTROLAB);
    processedData.push(headerRow);

    const METRICS: { key: keyof ReturnType<typeof calculateSummary>; label: string; astrolabSuffix?: string }[] = [
      { key: 'totalOrders', label: t(LANG_TEXT.TOTAL_ORDER) },
      { key: 'totalDeliveredOrder', label: t(LANG_TEXT.TOTAL_DELIVERED_ORDER) },
      { key: 'totalCancelledOrder', label: t(LANG_TEXT.TOTAL_CANCELLED_ORDER) },
      { key: 'totalDeliveryFee', label: t(LANG_TEXT.DELIVERY_FEE), astrolabSuffix: '(100%)' },
      { key: 'totalFoodRevenue', label: t(LANG_TEXT.FOOD_REVENUE), astrolabSuffix: '(10%)' },
      { key: 'totalOrderAmount', label: t(LANG_TEXT.TOTAL) },
      { key: 'totalDamagedOrder', label: t(LANG_TEXT.TOTAL_DAMAGED_ORDER) },
      { key: 'totalDamagedFee', label: t(LANG_TEXT.TOTAL_DAMAGED_FEE) },
    ];

    METRICS.forEach(({ key, label, astrolabSuffix }) => {
      const grandVal = grandStats[key] ?? 0;
      const metricRow: any = { [COL_A]: label };
      summaryTypeKeys.forEach((ot) => { metricRow[typeColKey[ot]] = tv(ot, key); });
      if (showForSuperAdmin) {
        if (key === 'totalFoodRevenue') {
          metricRow[COL_ASTROLAB] = `${Math.ceil(grandVal * 0.1)}${astrolabSuffix || ''}`;
        } else if (key === 'totalOrderAmount') {
          const adjustedDeliveryFee = grandStats.totalDeliveryFee ?? 0;
          const adjustedFoodRevenue = grandStats.totalFoodRevenue ?? 0;
          metricRow[COL_ASTROLAB] = adjustedDeliveryFee + Math.ceil(adjustedFoodRevenue * 0.1);
        } else {
          metricRow[COL_ASTROLAB] = astrolabSuffix ? `${grandVal}${astrolabSuffix}` : grandVal;
        }
      }
      processedData.push(metricRow);
    });


    return processedData;
  }, [csvData, showForSuperAdmin, showPermissionColumns, summaryData, routeType, t]);

  const handleDownload = async () => {
    const keys = Array.from(
      formattedData.reduce((acc: Set<string>, row: any) => {
        Object.keys(row || {}).forEach((k) => acc.add(k));
        return acc;
      }, new Set<string>())
    );

    const escapeCell = (val: any) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (s.includes(',') || s.includes('\n') || s.includes('"')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const header = keys.join(',');
    const rows = formattedData.map((row) => keys.map((k) => escapeCell((row || {})[k])).join(','));
    const csvString = [header, ...rows].join('\n');

    const filename = `data_order_history.csv`;
    await saveAndShareCsv(filename, csvString);
  };

  return (
    <div className={classes.Box}>
      <Button
        startIcon={<img src={DOWNLOAD_ICON} height={20} width={20} alt="DOWNLOAD_ICON" />}
        buttonStyle={buttonStyle}
        sxTextStyle={{
          color: 'var(--Base-White, #FFF)',
          fontWeight: 600,
          lineHeight: '20px',
          width: 'max-content',
        }}
        id="download-csv"
        fullWidth={false}
        title={t(LANG_TEXT.DOWNLOAD_CSV)}
        onClick={handleDownload}
      />
    </div>
  );
}

export default DownloadCsv;
